import { defineStore } from 'pinia';
import dayjs from 'dayjs';
import { LegalEntityType } from '@lw-modules/onboarding-platform';
import { useAccountListStore } from '../account-list-store';
import { useSettingsFormStore } from './settings-form-store';
import { useIndividualFormStore } from './individual-form-store';
import type { AddLegalEntityAccountBody } from '~/types';

export const useLegalEntityFormStore = defineStore('account-legal-entity-form', {
  state: () => ({
    allowedTypes: undefined as LegalEntityType[] | undefined,
    type: undefined as LegalEntityType | undefined,
    number: {
      value: undefined as string | undefined,
      isValid: undefined as boolean | undefined
    },
    country: undefined as string | undefined,
    name: {
      value: undefined as string | undefined,
      isValid: undefined as boolean | undefined
    },
    date: {
      value: undefined as string | undefined,
      isValid: undefined as boolean | undefined
    }
  }),

  getters: {
    addLegalEntityAccountBody(): AddLegalEntityAccountBody {
      const { profile, walletId, activityType } = useSettingsFormStore();
      const {
        firstName,
        lastName,
        email,
        birthDate,
        birthCountry,
        nationalities,
        residenceCountry
      } = useIndividualFormStore();

      return {
        legalEntity: {
          name: this.name.value,
          type: this.type,
          registrationNumber: this.number.value ?? undefined,
          registrationDate: dayjs(this.date.value, 'DD/MM/YYYY').format('YYYY-MM-DD'),
          address: {
            country: this.country
          }
        },
        legalRepresentative: {
          firstName: firstName.value,
          lastName: lastName.value,
          email: email.value,
          birthDate: dayjs(birthDate.value, 'DD/MM/YYYY').format('YYYY-MM-DD'),
          birthCountry,
          nationalities,
          address: {
            country: residenceCountry
          }
        },
        profile,
        walletId: walletId.value,
        activity: activityType
      };
    },
    isCompany(): boolean {
      return this.type === LegalEntityType.Company;
    }
  },

  actions: {
    getAllowedEntityTypes() {
      const { accountOptions } = useAccountListStore();
      const { profile, activityType } = useSettingsFormStore();

      const legalEntityOption = activityType
        ? accountOptions[activityType]?.['LEGAL-ENTITY']
        : undefined;

      if (profile && legalEntityOption?.profiles?.[profile]?.['entity-types']) {
        this.allowedTypes = legalEntityOption.profiles[profile]['entity-types'];
      } else {
        this.allowedTypes = undefined;
      }

      if (this.allowedTypes?.length === 1) {
        this.type = this.allowedTypes[0];
      }
    },
    validateForm(): boolean {
      const commonValidation = this.type && this.country && this.name.isValid && this.date.isValid;
      if (this.isCompany) {
        return (commonValidation && this.number.isValid) as boolean;
      }
      return (commonValidation && (!this.number.value || this.number.isValid)) as boolean;
    }
  }
});
